"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, LinearProgress,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import GraphicEqRounded from "@mui/icons-material/GraphicEqRounded";
import StopCircleRounded from "@mui/icons-material/StopCircleRounded";
import PauseRounded from "@mui/icons-material/PauseRounded";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import VolumeOffRounded from "@mui/icons-material/VolumeOffRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import { COACH_DATA_CHANNEL_ID, createCoachResponse, isCoachStopCommand, preferredPhoneInput } from "@/lib/voice/conversationCoach";
import { createMutedCoachTrack, disposeCoachAudio, setCoachListening } from "@/lib/voice/coachAudio";

const AUDIO_SETUP_KEY = "meex.conversationCoach.audioSetup.v1";

function readSavedAudioSetup() {
  try {
    const value = JSON.parse(localStorage.getItem(AUDIO_SETUP_KEY) || "null");
    return value && typeof value.deviceId === "string" && value.deviceId && value.outputConfirmed === true ? value : null;
  } catch {
    return null;
  }
}

function saveAudioSetup(value) {
  try { localStorage.setItem(AUDIO_SETUP_KEY, JSON.stringify(value)); } catch {}
}

export default function RealtimeVoiceTestButton({ onOpenChange }) {
  const [authorized, setAuthorized] = useState(false);
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [devices, setDevices] = useState([]);
  const [inputId, setInputId] = useState("");
  const [micLabel, setMicLabel] = useState("");
  const [level, setLevel] = useState(0);
  const [goal, setGoal] = useState("");
  const [style, setStyle] = useState("simple");
  const [busy, setBusy] = useState(false);
  const [hasContext, setHasContext] = useState(false);
  const [checkPlayed, setCheckPlayed] = useState(false);
  const [lastHeard, setLastHeard] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [replyMs, setReplyMs] = useState(null);
  const [savedAudioSetup, setSavedAudioSetup] = useState(null);
  const runRef = useRef(null);
  const setupRef = useRef(null);
  const panelRef = useRef(null);

  const stop = useCallback((preserveSetup = false) => {
    const run = runRef.current;
    runRef.current = null;
    if (run) disposeCoachAudio(run, { preserveSetup });
    if (preserveSetup && run?.stream) setupRef.current = run;
    else {
      if (setupRef.current && setupRef.current !== run) disposeCoachAudio(setupRef.current);
      setupRef.current = null;
    }
    setStatus(preserveSetup && setupRef.current ? "ready" : "idle");
    setBusy(false);
    setLevel(0);
    setHasContext(false);
    if (!preserveSetup) setCheckPlayed(false);
    setLastHeard("");
    setSuggestion("");
    setReplyMs(null);
  }, []);

  useEffect(() => () => { stop(); onOpenChange?.(false); }, [stop, onOpenChange]);
  useEffect(() => { setSavedAudioSetup(readSavedAudioSetup()); }, []);
  useEffect(() => {
    if (open) panelRef.current?.parentElement?.scrollTo({ top: 0 });
  }, [open]);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/support/realtime-voice-test", { cache: "no-store", signal: controller.signal })
      .then((response) => (response.ok ? response.json() : { allowed: false }))
      .then((result) => { if (!controller.signal.aborted) setAuthorized(result.allowed === true); })
      .catch(() => {});
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const end = (message) => {
      if (!runRef.current && !setupRef.current) return;
      stop();
      setNotice(message);
    };
    const hidden = () => {
      if (document.hidden) end("Session ended when the app left the screen. Keep this screen open while coaching.");
    };
    const devicesChanged = () => {
      if (runRef.current?.confirmed) {
        try { localStorage.removeItem(AUDIO_SETUP_KEY); } catch {}
        setSavedAudioSetup(null);
        end("Audio devices changed. Recheck the microphone and earbuds before restarting.");
      } else if (runRef.current?.stream) {
        try { localStorage.removeItem(AUDIO_SETUP_KEY); } catch {}
        setSavedAudioSetup(null);
        end("Audio devices changed during setup. Repeat the microphone and earbud checks before listening.");
      } else if (setupRef.current) {
        try { localStorage.removeItem(AUDIO_SETUP_KEY); } catch {}
        setSavedAudioSetup(null);
        end("Audio devices changed. Recheck the microphone and earbuds before restarting.");
      }
    };
    const pageHidden = () => end("Session ended. Start again when you return.");
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("pagehide", pageHidden);
    navigator.mediaDevices?.addEventListener("devicechange", devicesChanged);
    return () => {
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("pagehide", pageHidden);
      navigator.mediaDevices?.removeEventListener("devicechange", devicesChanged);
    };
  }, [stop]);

  const fail = (run, message) => {
    if (runRef.current !== run) return;
    stop();
    setError(message);
  };
  const clearAudioSetup = () => {
    stop();
    try { localStorage.removeItem(AUDIO_SETUP_KEY); } catch {}
    setSavedAudioSetup(null);
    setDevices([]);
    setInputId("");
    setMicLabel("");
    setSettingsOpen(false);
    setNotice("Audio setup cleared. Check the microphone to choose a new setup.");
  };
  const cancelSuggestion = () => {
    const run = runRef.current;
    if (!run) return;
    run.pendingAction = null;
    run.cancelled = true;
    if (run.audio) run.audio.muted = true;
    if (run.channel?.readyState === "open") {
      if (run.inFlight && run.responseId) run.channel.send(JSON.stringify({ type: "response.cancel", response_id: run.responseId }));
      run.channel.send(JSON.stringify({ type: "output_audio_buffer.clear" }));
    }
    clearTimeout(run.replyTimeout);
    // Keep the next action blocked until cancellation is acknowledged.
    run.busy = Boolean(run.inFlight);
    setBusy(run.busy);
    if (run.inFlight) {
      clearTimeout(run.cancelTimeout);
      run.cancelTimeout = setTimeout(() => fail(run, "The coach did not acknowledge cancellation. Start a fresh session."), 3000);
    }
  };
  const dispatchAction = (run, action) => {
    run.pendingAction = null;
    run.action = action;
    run.busy = true;
    run.inFlight = true;
    run.cancelled = false;
    run.responseId = null;
    run.audioComplete = false;
    run.audio.muted = false;
    run.audioStarted = false;
    if (action === "check") run.requestedAt = performance.now();
    run.transcript = "";
    setBusy(true);
    if (action === "check") setCheckPlayed(false);
    else { setSuggestion(""); setReplyMs(null); }
    run.channel.send(JSON.stringify(createCoachResponse(action, run.options)));
    clearTimeout(run.replyTimeout);
    run.replyTimeout = setTimeout(() => {
      if (runRef.current !== run) return;
      cancelSuggestion();
      setError("The suggestion timed out. Try again when the connection is stable.");
    }, 20_000);
  };
  const requestAction = (action) => {
    const run = runRef.current;
    if (!run || run.busy || run.channel?.readyState !== "open") return;
    setError("");
    run.requestedAt = performance.now();
    if (action !== "check" && run.speaking) {
      run.pendingAction = action;
      run.busy = true;
      setBusy(true);
      run.replyTimeout = setTimeout(() => {
        if (runRef.current !== run) return;
        cancelSuggestion();
        setNotice("Wait for the person to finish, then tap again.");
      }, 20_000);
      return;
    }
    dispatchAction(run, action);
  };

  const prepareMicrophone = async (deviceId = "", { reuseConfirmation = false } = {}) => {
    stop();
    setError("");
    setNotice("");
    const run = { controller: new AbortController(), listening: false, busy: false };
    runRef.current = run;
    const current = () => runRef.current === run;
    setStatus("preparing");
    run.timeout = setTimeout(() => fail(run, "Microphone setup timed out. Allow microphone access and try again."), 30_000);
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
        throw new Error("Open this page in Safari over HTTPS and allow microphone access.");
      }
      // Create/resume inside the tap gesture; Safari can suspend contexts created after permission prompts.
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        run.audioContext = new AudioContext();
        run.audioContext.resume().catch(() => {});
      }
      const capture = async (id) => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: {
          ...(id ? { deviceId: { exact: id } } : {}),
          echoCancellation: true, noiseSuppression: true, autoGainControl: true,
        } });
        if (!current()) { stream.getTracks().forEach((track) => track.stop()); return null; }
        return stream;
      };
      let selectedDeviceId = deviceId;
      let reuseOutputConfirmation = reuseConfirmation && Boolean(selectedDeviceId);
      try {
        run.stream = await capture(selectedDeviceId);
      } catch (cause) {
        if (!selectedDeviceId || !["NotFoundError", "OverconstrainedError"].includes(cause.name)) throw cause;
        try { localStorage.removeItem(AUDIO_SETUP_KEY); } catch {}
        setSavedAudioSetup(null);
        selectedDeviceId = "";
        reuseOutputConfirmation = false;
        run.stream = await capture("");
      }
      if (!current() || !run.stream) return;
      if (!reuseOutputConfirmation || !selectedDeviceId) {
        const available = await navigator.mediaDevices.enumerateDevices();
        if (!current()) return;
        setDevices(available.filter((device) => device.kind === "audioinput"));
        const preferred = preferredPhoneInput(available);
        if (!selectedDeviceId && preferred?.deviceId && run.stream.getAudioTracks()[0].getSettings().deviceId !== preferred.deviceId) {
          setInputId(preferred.deviceId);
          run.stream.getTracks().forEach((track) => track.stop());
          run.stream = await capture(preferred.deviceId);
          if (!current() || !run.stream) return;
        }
      }
      const track = run.stream.getAudioTracks()[0];
      track.addEventListener("ended", () => fail(run, "Microphone disconnected. Check audio devices and restart."));
      const activeDeviceId = track.getSettings().deviceId || selectedDeviceId;
      const activeMicLabel = track.label || "System microphone";
      setInputId(activeDeviceId);
      setMicLabel(activeMicLabel);
      if (reuseOutputConfirmation) run.outputConfirmed = true;
      if (run.audioContext) {
        run.source = run.audioContext.createMediaStreamSource(run.stream);
        const analyser = run.audioContext.createAnalyser();
        analyser.fftSize = 256;
        run.source.connect(analyser); // Never connect the microphone to speakers.
        const samples = new Uint8Array(analyser.fftSize);
        run.meterTimer = setInterval(() => {
          if (!current()) return;
          analyser.getByteTimeDomainData(samples);
          const rms = Math.sqrt(samples.reduce((sum, sample) => sum + ((sample - 128) / 128) ** 2, 0) / samples.length);
          setLevel(Math.min(100, rms * 450));
        }, 100);
      }
      clearTimeout(run.timeout);
      setStatus("ready");
      if (reuseOutputConfirmation) {
        setCheckPlayed(true);
        connect();
      }
    } catch (cause) {
      fail(run, cause.name === "NotAllowedError"
        ? "Microphone access was denied. Allow it in Safari’s website settings and try again."
        : cause.message || "Could not open the microphone.");
    }
  };

  const connect = async () => {
    const run = runRef.current || setupRef.current;
    if (!run?.stream || run.peer) return;
    runRef.current = run;
    run.controller = new AbortController();
    run.listening = false;
    run.busy = false;
    run.inFlight = false;
    run.speaking = false;
    const current = () => runRef.current === run;
    setError("");
    setStatus("connecting");
    run.audioContext?.resume().catch(() => {});
    run.options = { goal: goal.trim(), style };
    run.timeout = setTimeout(() => fail(run, "Voice connection timed out. Check your network and try again."), 20_000);
    try {
      run.audio = new Audio();
      run.audio.autoplay = true;
      run.audio.playsInline = true;
      const peer = new RTCPeerConnection();
      run.peer = peer;
      // Meter the original locally; send only this initially disabled clone.
      run.sendTrack = createMutedCoachTrack(run.stream);
      peer.addTrack(run.sendTrack, new MediaStream([run.sendTrack]));
      peer.ontrack = (event) => {
        if (!current()) return;
        run.audio.srcObject = event.streams[0] || new MediaStream([event.track]);
        run.audio.play().catch(() => fail(run, "Audio playback was blocked. Restart and tap Connect again."));
      };
      peer.addEventListener("connectionstatechange", () => {
        if (!current()) return;
        clearTimeout(run.disconnectTimeout);
        if (peer.connectionState === "failed") fail(run, "Voice connection failed. Restart when the network is stable.");
        if (peer.connectionState === "disconnected") {
          run.disconnectTimeout = setTimeout(() => fail(run, "Voice connection was lost. Please restart."), 5000);
        }
      });
      const channel = peer.createDataChannel("oai-events", {
        negotiated: true,
        id: COACH_DATA_CHANNEL_ID,
      });
      run.channel = channel;
      channel.addEventListener("open", () => {
        if (!current()) return;
        clearTimeout(run.timeout);
        if (run.outputConfirmed) {
          run.confirmed = true;
          setCoachListening(run, true);
          setStatus("listening");
          setNotice("Listening. Tap Reply, Explain, or Rephrase after a conversation turn.");
          navigator.wakeLock?.request("screen").then((lock) => {
            if (runRef.current === run) run.wakeLock = lock;
            else lock.release().catch(() => {});
          }).catch(() => {});
        } else {
          setStatus("check-output");
          dispatchAction(run, "check");
        }
      });
      channel.addEventListener("close", () => fail(run, "Voice session ended. You can start a fresh session."));
      channel.addEventListener("error", () => fail(run, "Voice connection failed. Please restart."));
      channel.addEventListener("message", (event) => {
        if (!current()) return;
        let data;
        try { data = JSON.parse(event.data); } catch { return; }
        if (data.type === "error") {
          if (["response_cancel_not_active", "output_audio_buffer_clear_not_active"].includes(data.error?.code)) return;
          fail(run, data.error?.message || "Voice session failed.");
        } else if (data.type === "input_audio_buffer.speech_started" && run.listening) {
          run.speaking = true;
        } else if (data.type === "input_audio_buffer.speech_stopped") {
          run.speaking = false;
        } else if (data.type === "input_audio_buffer.committed" && run.listening) {
          run.latestInputId = data.item_id;
          run.hasContext = true;
          setHasContext(true);
          if (run.pendingAction) dispatchAction(run, run.pendingAction);
        } else if (data.type === "conversation.item.input_audio_transcription.completed" && run.listening) {
          if (isCoachStopCommand(data.transcript)) {
            stop(true);
            setNotice('Session ended by “Cheers”.');
          } else if (data.item_id === run.latestInputId) {
            setLastHeard(data.transcript || "");
          }
        } else if (data.type === "conversation.item.input_audio_transcription.failed") {
          setNotice('Could not transcribe that turn. “Cheers” may not work; use the stop icon.');
        } else if (data.type === "response.created") {
          run.responseId = data.response?.id;
          if (run.cancelled) channel.send(JSON.stringify({ type: "response.cancel", response_id: run.responseId }));
        } else if (data.type === "response.output_audio_transcript.delta" && run.action !== "check" && !run.cancelled && data.response_id === run.responseId) {
          run.transcript += data.delta || "";
          setSuggestion(run.transcript);
        } else if (data.type === "output_audio_buffer.started" && data.response_id === run.responseId) {
          run.audioStarted = true;
          if (run.cancelled) channel.send(JSON.stringify({ type: "output_audio_buffer.clear" }));
          else if (run.action !== "check" && run.requestedAt) setReplyMs(Math.round(performance.now() - run.requestedAt));
        } else if ((data.type === "output_audio_buffer.stopped" || data.type === "output_audio_buffer.cleared") && data.response_id === run.responseId) {
          clearTimeout(run.replyTimeout);
          run.audioComplete = true;
          run.busy = Boolean(run.inFlight);
          setBusy(run.busy);
          if (run.action === "check" && !run.cancelled && run.audioStarted && data.type === "output_audio_buffer.stopped") setCheckPlayed(true);
        } else if (data.type === "response.done" && data.response?.id === run.responseId) {
          clearTimeout(run.cancelTimeout);
          run.inFlight = false;
          if (data.response.status !== "completed" || run.cancelled) {
            clearTimeout(run.replyTimeout);
            run.busy = false;
            setBusy(false);
            if (data.response.status === "failed") setError("Could not generate a suggestion. Try again.");
            if (data.response.status === "incomplete") setError("The suggestion was cut short. Try again.");
          } else if (run.audioComplete) {
            run.busy = false;
            setBusy(false);
          }
        }
      });
      const offer = await peer.createOffer();
      if (!current()) return;
      await peer.setLocalDescription(offer);
      if (!current()) return;
      const response = await fetch("/api/support/realtime-voice-test", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sdp: offer.sdp, dcid: COACH_DATA_CHANNEL_ID, ...run.options }), signal: run.controller.signal,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Could not connect the coach.");
      }
      const answerSdp = await response.text();
      if (!current()) return;
      await peer.setRemoteDescription({ type: "answer", sdp: answerSdp });
    } catch (cause) { fail(run, cause.message || "Could not connect the coach."); }
  };

  const confirmHeadphones = () => {
    const run = runRef.current;
    if (!run || !checkPlayed || run.busy) return;
    run.confirmed = true;
    run.outputConfirmed = true;
    const preference = {
      deviceId: run.stream.getAudioTracks()[0].getSettings().deviceId || inputId || "",
      micLabel: run.stream.getAudioTracks()[0].label || micLabel,
      outputConfirmed: true,
    };
    saveAudioSetup(preference);
    setSavedAudioSetup(preference);
    connect();
  };
  const playHeadphoneCheck = () => {
    const run = runRef.current;
    if (!run) return;
    if (run.checkOscillator) return;
    if (!run.audioContext) {
      setError("Could not play the audio check in this browser.");
      return;
    }
    setError("");
    setNotice("");
    setCheckPlayed(false);
    setStatus("check-output");
    const context = run.audioContext;
    const output = context.createMediaStreamDestination();
    run.checkOutput = output;
    const player = new Audio();
    player.autoplay = true;
    player.playsInline = true;
    player.srcObject = output.stream;
    run.checkPlayer = player;
    const playback = player.play();
    context.resume().then(() => playback).then(() => {
      if (runRef.current !== run) return;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      run.checkOscillator = oscillator;
      const startAt = context.currentTime + 0.03;
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(660, startAt);
      oscillator.frequency.setValueAtTime(880, startAt + 0.32);
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(0.12, startAt + 0.025);
      gain.gain.setValueAtTime(0.12, startAt + 0.55);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.62);
      oscillator.connect(gain);
      gain.connect(output);
      oscillator.addEventListener("ended", () => {
        oscillator.disconnect();
        gain.disconnect();
        run.checkOscillator = null;
        player.pause();
        player.srcObject = null;
        run.checkPlayer = null;
        output.stream.getTracks().forEach((track) => track.stop());
        run.checkOutput = null;
        if (runRef.current === run) setCheckPlayed(true);
      }, { once: true });
      oscillator.start(startAt);
      oscillator.stop(startAt + 0.64);
    }).catch(() => {
      player.pause();
      player.srcObject = null;
      run.checkPlayer = null;
      output.stream.getTracks().forEach((track) => track.stop());
      run.checkOutput = null;
      setError("Could not play the audio check in this browser.");
    });
  };
  const togglePause = () => {
    const run = runRef.current;
    if (!run) return;
    setCoachListening(run, !run.listening);
    run.speaking = false;
    cancelSuggestion();
    run.channel.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
    setStatus(run.listening ? "listening" : "paused");
  };

  if (!authorized) return null;
  const running = status === "listening" || status === "paused";
  const close = () => { stop(); setOpen(false); onOpenChange?.(false); };
  const labels = {
    idle: "", preparing: "Opening microphone…", ready: setupRef.current?.outputConfirmed ? "Ready to restart" : "Microphone ready",
    connecting: "Connecting…", "check-output": "Audio check", listening: "Listening", paused: "Paused",
  };
  return (
    <>
      <Tooltip title={open ? "End and close conversation coach" : "Open conversation coach"}>
        <IconButton aria-label={open ? "End and close conversation coach" : "Open conversation coach"}
          aria-pressed={open} size="small" color={open ? "error" : "default"}
          onClick={open ? close : () => { setOpen(true); onOpenChange?.(true); }}
          sx={{
            position: "absolute", top: 8, left: 8,
            // Support's opaque sticky content uses layers up to 7.
            zIndex: (theme) => theme.zIndex.modal + 2,
            bgcolor: "background.paper", boxShadow: 1,
          }}>
          {open ? <StopCircleRounded /> : <GraphicEqRounded />}
        </IconButton>
      </Tooltip>
      {open && (
        <Stack ref={panelRef} className="ph-no-capture" spacing={1.5} sx={{ px: { xs: 2.5, sm: 4 }, pt: 7, pb: 3 }}>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Conversation coach</Typography>
            {!running && status !== "check-output" && status !== "connecting" && (
              <Tooltip title="Preferences">
                <IconButton size="small" aria-label="Preferences" aria-expanded={settingsOpen}
                  onClick={() => setSettingsOpen((value) => !value)}><TuneRounded fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Stack>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
          {notice && <Alert severity="info" onClose={() => setNotice("")}>{notice}</Alert>}
          {labels[status] && <Typography variant="body2" color="text.secondary" role="status">{busy && running ? "Preparing…" : labels[status]}</Typography>}
          {settingsOpen && !running && status !== "check-output" && status !== "connecting" && (
            <>
              <TextField label="Context" size="small" multiline minRows={2}
                value={goal} onChange={(event) => setGoal(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 1000 } }} disabled={status === "connecting"} />
              <TextField select label="English style" size="small" value={style}
                onChange={(event) => setStyle(event.target.value)} disabled={status === "connecting"}>
                <MenuItem value="simple">Simple</MenuItem>
                <MenuItem value="natural">Natural</MenuItem>
              </TextField>
              <Button size="small" onClick={clearAudioSetup}>Change audio setup</Button>
            </>
          )}
          {(status === "ready" || (status === "idle" && devices.length > 0) || running || status === "check-output") && (
            <Box>
              {status === "ready" || status === "idle" ? (
                <TextField select fullWidth size="small" label="Microphone" value={inputId}
                  onChange={(event) => prepareMicrophone(event.target.value)}>
                  {!devices.some((device) => device.deviceId === inputId) && <MenuItem value={inputId}>{micLabel || "System microphone"}</MenuItem>}
                  {devices.map((device, index) => <MenuItem key={device.deviceId || index} value={device.deviceId}>{device.label || `Microphone ${index + 1}`}</MenuItem>)}
                </TextField>
              ) : <Typography variant="caption" color="text.secondary">{micLabel}</Typography>}
              {status !== "idle" && <LinearProgress variant="determinate" value={level} aria-label="Microphone level" sx={{ mt: 1.5, height: 5, borderRadius: 4 }} />}
            </Box>
          )}
          {status === "idle" && (savedAudioSetup
            ? <Button variant="contained" onClick={() => prepareMicrophone(savedAudioSetup.deviceId, { reuseConfirmation: true })}>Start with last setup</Button>
            : <Button variant="contained" onClick={() => prepareMicrophone()}>Check microphone</Button>)}
          {status === "preparing" && <CircularProgress size={22} aria-label="Opening microphone" />}
          {status === "ready" && <Button variant="contained" onClick={setupRef.current?.outputConfirmed ? connect : playHeadphoneCheck}>{setupRef.current?.outputConfirmed ? "Start listening" : "Check earbuds"}</Button>}
          {status === "ready" && <Typography variant="caption" color="text.secondary">Mic stays connected; audio is sent only while listening.</Typography>}
          {status === "connecting" && <CircularProgress size={22} aria-label="Connecting" />}
          {status === "check-output" && (
            <>
              <Button variant="contained" disabled={!checkPlayed || busy} onClick={confirmHeadphones}>Heard in AirPods · Connect</Button>
              <Button size="small" disabled={busy} onClick={playHeadphoneCheck}>Play again</Button>
            </>
          )}
          {running && (
            <>
              <Stack direction="row" spacing={1}>
                {[["reply", "Reply"], ["explain", "Explain"], ["rephrase", "Rephrase"]].map(([action, label]) => (
                  <Button key={action} variant={action === "reply" ? "contained" : "outlined"}
                    disabled={!hasContext || busy} onClick={() => requestAction(action)} sx={{ flex: 1, minWidth: 0 }}>{label}</Button>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Button size="small" startIcon={status === "paused" ? <PlayArrowRounded /> : <PauseRounded />} onClick={togglePause}>
                  {status === "paused" ? "Resume" : "Pause"}
                </Button>
                <Tooltip title="Stop the suggestion audio"><span>
                  <IconButton aria-label="Stop suggestion audio" disabled={!busy} onClick={cancelSuggestion}><VolumeOffRounded /></IconButton>
                </span></Tooltip>
              </Stack>
              {lastHeard && <Box><Typography variant="caption" color="text.secondary">Last heard</Typography><Typography variant="body2">{lastHeard}</Typography></Box>}
              {suggestion && <Box aria-live="polite"><Typography variant="caption" color="text.secondary">Suggestion</Typography><Typography sx={{ fontSize: 18, lineHeight: 1.5 }}>{suggestion}</Typography></Box>}
              {replyMs !== null && <Tooltip title="Tap to server audio start; excludes device playback delay"><Typography variant="caption" color="text.secondary">{replyMs} ms</Typography></Tooltip>}
            </>
          )}
          {status !== "idle" && status !== "ready" && <Button size="small" color="error" onClick={() => stop(true)}>End</Button>}
        </Stack>
      )}
    </>
  );
}
